import React, { useEffect, useState, useCallback } from 'react';
import { getOpenQuestions, answerQuestion, dismissQuestion, QaOpenQuestion } from '../services/api';

interface Props {
  threadId: number;
  // Bump/change this to force a re-fetch (e.g. when the feed refreshes). Mirrors
  // SuggestedReply's refreshKey.
  refreshKey?: string | number | null;
  // Notify the parent after an answer/dismiss so it can refresh the feed (a newly
  // promoted KB entry may seed an AI draft reply).
  onAfterAction?: () => void;
  // Reports whether there is currently an open question to show (self-hides when
  // empty). Lets the parent gate the "Generated messages" section on real content.
  onHasContent?: (has: boolean) => void;
}

// One open question the AI couldn't answer. Its own answer text, scope choice,
// busy + error state, and independent Answer / Dismiss controls. Lifting this out
// keeps per-question state isolated so acting on one never disturbs another —
// same approach as SuggestedReply's DraftCard.
const QuestionCard: React.FC<{ question: QaOpenQuestion; onAfterAction: () => void }> = ({ question, onAfterAction }) => {
  const [answer, setAnswer] = useState('');
  const [scope, setScope] = useState<'general' | 'customer'>('general');
  // Track WHICH action is in flight so the Answer button doesn't read
  // "Saving…" while a Dismiss is running, and vice-versa.
  const [busyKind, setBusyKind] = useState<null | 'answer' | 'dismiss'>(null);
  const busy = busyKind !== null;
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Keyed by question.id in the parent, so a different question mounts a fresh
  // instance and the rep's in-progress answer is never clobbered by a refetch.
  if (done) return null;

  const act = async (kind: 'answer' | 'dismiss', fn: () => Promise<any>) => {
    setBusyKind(kind);
    setError('');
    try {
      await fn();
      setDone(true);
      onAfterAction();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Action failed');
    } finally {
      setBusyKind(null);
    }
  };

  const answerEmpty = answer.trim() === '';
  const textareaId = `qa-answer-${question.id}`;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2 mb-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-amber-800">
          ❓ AI needs an answer to learn
        </span>
        {question.category && (
          <span className="text-xs text-gray-500">· {question.category}</span>
        )}
      </div>
      <p className="text-sm text-gray-800 mb-2">{question.question_text}</p>
      <label htmlFor={textareaId} className="sr-only">Answer to: {question.question_text}</label>
      <textarea
        id={textareaId}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={3}
        disabled={busy}
        placeholder="Type the answer so the AI can use it next time…"
        className="w-full text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60"
      />
      <fieldset className="mt-2" disabled={busy}>
        <legend className="text-xs font-medium text-gray-600 mb-1">Who should this answer apply to?</legend>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name={`qa-scope-${question.id}`}
              value="general"
              checked={scope === 'general'}
              onChange={() => setScope('general')}
              className="text-amber-600 focus:ring-amber-500"
            />
            General — everyone
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name={`qa-scope-${question.id}`}
              value="customer"
              checked={scope === 'customer'}
              onChange={() => setScope('customer')}
              className="text-amber-600 focus:ring-amber-500"
            />
            This customer only
          </label>
        </div>
      </fieldset>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      <div className="flex items-center justify-end gap-2 mt-2">
        <button
          onClick={() => act('dismiss', () => dismissQuestion(question.id))}
          disabled={busy}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-red-600 disabled:opacity-50"
        >
          {busyKind === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
        </button>
        <button
          onClick={() => act('answer', () => answerQuestion(question.id, answer.trim(), scope))}
          disabled={busy || answerEmpty}
          className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-full hover:bg-amber-700 disabled:opacity-50"
        >
          {busyKind === 'answer' ? 'Saving…' : 'Answer'}
        </button>
      </div>
    </div>
  );
};

// Thread-scoped: shows EVERY open question the AI logged for this sub-thread so a
// human can answer (promoting it to the reusable KB) or dismiss it. Hides itself
// entirely when there are none — mirrors SuggestedReply.
const OpenQuestion: React.FC<Props> = ({ threadId, refreshKey, onAfterAction, onHasContent }) => {
  const [questions, setQuestions] = useState<QaOpenQuestion[]>([]);

  const load = useCallback(() => {
    getOpenQuestions(threadId)
      .then((qs) => setQuestions(qs || []))
      .catch(() => { /* surfaced on next load */ });
  }, [threadId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Report content presence to the parent (for the collapsible section gating).
  useEffect(() => { onHasContent?.(questions.length > 0); }, [questions.length, onHasContent]);

  if (questions.length === 0) return null;

  // Refetch after any action so the list reflects the server (an answered/
  // dismissed question drops out) and notify the parent to refresh the feed.
  const afterAction = () => {
    if (onAfterAction) onAfterAction();
    load();
  };

  return (
    <>
      {questions.map((q) => (
        <QuestionCard key={q.id} question={q} onAfterAction={afterAction} />
      ))}
    </>
  );
};

export default OpenQuestion;
